
import _orderBy from 'lodash.orderby';

import {
  saveAssetInLocalStorage,
  deleteAssetFromLocalStorage,
  createS3MultipartUpload,
  getPartSignedUrls,
  completeS3MultipartUpload } from
'../utils';
import { HUNDRED_MB_FILE_CHUNK_SIZE, TEN_MB_FILE_CHUNK_SIZE } from '../constants';
import createPartUploader from './PartUploader';
























const RETRY_INTERVAL = 3000;
const MAX_RETRY_INTERVAL = 30000;
// A stalled upload is abandoned once retries have spanned this long, so it
// surfaces as an error the user can action rather than retrying invisibly.
const RETRY_WINDOW = 150000;

export default class FileUploader {






































  constructor({
    addUploadTasksToQueue,
    removeUploadTasksFromQueue,
    apiClient,
    progress,
    error,
    complete,
    file,
    assetId,
    organisationId,
    bucketKey,
    metaData,
    cacheUploadId,
    uploadedParts = [],
    uploadedBytes = 0,
    ORIGINAL
  }) {
    this._apiClient = apiClient;
    this._addUploadTasksToQueue = addUploadTasksToQueue;
    this._removeUploadTasksFromQueue = removeUploadTasksFromQueue;

    this._progress = progress;
    this._error = error;
    this._complete = complete;

    this._assetId = assetId;
    this._organisationId = organisationId;
    this._file = file;
    this._bucketKey = bucketKey;
    this._metaData = metaData;

    this._chunkSize = this._file.size >= HUNDRED_MB_FILE_CHUNK_SIZE ?
    HUNDRED_MB_FILE_CHUNK_SIZE : TEN_MB_FILE_CHUNK_SIZE;

    this._chunksCount = Math.floor(this._file.size / this._chunkSize) + 1;

    this._fileBytesUploaded = uploadedBytes;
    this._uploadedParts = uploadedParts;
    this._partUploaders = [];
    this._uploadId = cacheUploadId || '';
    this._ORIGINAL = ORIGINAL;
  }

  /**
   * Initiate file upload
   * @return {Promise<void>}
   */
  start = async () => {
    try {
      if (!this._uploadId) {
        const result = await this.createS3MultipartUploadWithRetries();

        if (this._destroyed) {return;}

        if (!result) {throw new Error('Could not start the upload');}

        this._uploadId = result;

        saveAssetInLocalStorage(this._assetId, this.getLocalCache());
      }
      await this.addUploadPartsTasks();
    } catch (e) {
      this.setStatusToError();
    }
  };

  /**
   * Retry a task with exponential backoff until it succeeds, the uploader is
   * destroyed, or RETRY_WINDOW is spent. Rejects once the window is spent, so
   * callers surface the failure instead of retrying forever.
   * @param {Function} task
   * @return {Promise<any>}
   */
  retryWithBackoff = (task) => new Promise((resolve, reject) => {
    let waited = 0;
    let interval = RETRY_INTERVAL;

    const attempt = async () => {
      if (this._destroyed) {return void resolve();}

      try {
        return void resolve(await task());
      } catch (e) {
        if (waited >= RETRY_WINDOW) {return void reject(e);}

        waited += interval;

        const delay = interval;

        interval = Math.min(interval * 2, MAX_RETRY_INTERVAL);

        return void setTimeout(attempt, delay);
      }
    };

    attempt();
  });

  createS3MultipartUploadWithRetries = () => this.retryWithBackoff(() =>
  createS3MultipartUpload(
    this._apiClient,
    this._bucketKey,
    this._file.type,
    this._organisationId
  ));

  destroy = () => {
    this._destroyed = true;

    this.abortFileUploading();
  };

  getLocalCache = () => ({
    assetId: this._assetId,
    uploadId: this._uploadId,
    bucketKey: this._bucketKey,
    uploadedParts: this._uploadedParts,
    uploadedBytes: this._fileBytesUploaded,
    filename: this._file.name,
    metaData: this._metaData,
    lastModified: this._file.lastModified,
    assetFiles: { ORIGINAL: this._ORIGINAL }
  });

  updateUploadedBytes = (value) => {
    this._fileBytesUploaded = value;

    this._progress(this._fileBytesUploaded);
  };

  handlePartUploaded = (part) => {
    this._uploadedParts.push(part);

    saveAssetInLocalStorage(this._assetId, this.getLocalCache());

    if (this._uploadedParts.length !== this._chunksCount) {
      return null;
    }

    return this.completeFileUpload(this._assetId);
  };

  /**
   * Abort all file uploading parts
   * @return {*}
   */
  abortFileUploading = () => {
    this._partUploaders.forEach((partUploader) => partUploader.xhr && partUploader.xhr.abort());
    this._partUploaders = [];

    this._removeUploadTasksFromQueue(this._assetId);
  };

  setStatusToError = (bytes) => {
    this.abortFileUploading();

    if (!bytes) {
      this._error();
      return;
    }

    this._error(bytes);
  };

  /**
   * Return file chunk
   * @param {number} partNumber
   * @return {Blob}
   */
  getFileBlob = (partNumber) => {
    const start = (partNumber - 1) * this._chunkSize;
    const end = partNumber * this._chunkSize;

    return partNumber < this._chunksCount ? this._file.slice(start, end) : this._file.slice(start);
  };

  completeS3MultipartUploadWithRetries =
  (parts, assetIdToTranscode) => this.retryWithBackoff(() =>
  completeS3MultipartUpload(
    this._apiClient,
    this._uploadId,
    this._bucketKey,
    this._organisationId,
    parts,
    assetIdToTranscode
  ));

  /**
   * Completes s3 multipart upload
   * @param {string} assetId
   * @return {Promise<void>}
   */
  completeFileUpload = async (assetId) => {
    if (this._destroyed) {return;}

    const parts = _orderBy(this._uploadedParts, 'PartNumber');

    const isVideo = this._file.type.includes('video/')
    // $FlowFixMe
    || this._file.extension && ['mts'].includes(this._file.extension.toLowerCase());

    // Reached from a part handler rather than start(), so failures are caught
    // here instead of bubbling to start()'s catch.
    let result;

    try {
      result = await this.completeS3MultipartUploadWithRetries(
        parts,
        isVideo ? assetId : null
      );
    } catch (e) {
      return void this.setStatusToError(this._fileBytesUploaded);
    }

    if (this._destroyed) {return;}

    if (!result) {return void this.setStatusToError(this._fileBytesUploaded);}

    deleteAssetFromLocalStorage(this._assetId);

    this._complete(isVideo);
  };

  /**
   * Get array with not uploaded part numbers
   * @return {Array}
   */
  calculatePartsToUpload = () => {
    // Create 1, 2... n+1 array
    const partsArray = Array.from(Array(this._chunksCount).keys()).map((i) => i + 1);

    const parts = [];

    partsArray.forEach((partNumber) => {
      // Skip, if part vas uploaded
      if (this._uploadedParts.some(({ PartNumber }) => PartNumber === partNumber)) {
        return;
      }

      parts.push(partNumber);
    });

    return parts;
  };

  addUploadPartsTasks = () => {
    if (this._destroyed) {return Promise.resolve();}

    const partsToUpload = this.calculatePartsToUpload();

    if (!partsToUpload.length && this._uploadedParts.length === this._chunksCount) {
      return this.completeFileUpload(this._assetId);
    }

    return this.addUploadPartsTasksToQueue(partsToUpload);
  };

  addUploadPartsTasksToQueue = async (parts) => {
    if (this._destroyed) {return;}

    const urls = await getPartSignedUrls(
      this._apiClient,
      this._uploadId,
      this._bucketKey,
      this._organisationId,
      parts
    );

    if (this._destroyed) {return;}

    if (!urls) {throw new Error('Could not get upload part URLs');}

    const tasks = urls.reduce((tasksArr, { partNumber, url }) => [
    ...tasksArr,
    this.createUploadPartTask(partNumber, url)],
    []);

    return void this._addUploadTasksToQueue(this._assetId, tasks);
  };

  createPartHandlers = () => ({
    onComplete: this.handlePartUploaded,
    onProgress: (bytes) => {
      this.updateUploadedBytes(this._fileBytesUploaded + bytes);
    },
    // Retries are driven by uploadPartWithRetries; a part that keeps failing
    // surfaces through createUploadPartTask once the retry window is spent.
    onError: () => {}
  });

  uploadPartWithRetries = (partUploader) => this.retryWithBackoff(
    () => partUploader.uploadPart()
  );

  /**
   * Creates a task for file part uploading
   * @param {string} partNumber
   * @param {string} url
   * @return {Function}
   */
  createUploadPartTask = (partNumber, url) => async (done) => {
    if (this._destroyed) {return void done();}

    const handlers = this.createPartHandlers();

    try {
      const partUploader = createPartUploader({
        ...handlers,
        partNumber,
        url,
        blob: this.getFileBlob(partNumber)
      });

      this._partUploaders[partNumber] = partUploader;

      await this.uploadPartWithRetries(partUploader);
    } catch (e) {
      return void this.setStatusToError(this._fileBytesUploaded);
    }

    return void done();
  };
}